/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 * 
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

/**
 * This class exposes "hooks", which are app events that can be stopped or overridden. This can be anything from object clicks, key presses, etc...
 * 
 * Registered handlers can have priorities as well.
 */
export default new class Hooks {

    /** Registered hooks */
    all = {}

    /** 
     * Get a named hook. 
     * 
     * @returns {Hook} The hook.
     */
    get(name) {

        // Find it
        let hook = this.all[name]
        if (hook)
            return hook

        // Not found, create it
        hook = new Hook()
        hook.name = name
        this.all[name] = hook

        // Done
        return hook

    }

}

/** Represents an individual hook */
class Hook {

    /** Hook name */
    name = ''

    /** Registered handlers */
    handlers = []

    /** 
     * Register a handler. 
     * 
     * @param {Function} callback Function to be called when this hook is triggered. Return true from this function to cancel the event.
     * @param {Number} priority The priority of this handler
     */
    addHandler(callback, priority = 0) {

        // Callback is not a function
        if (!callback || typeof callback != 'function') {
            console.warn('[Hooks] Callback function must be specified when adding handler.', `No handler added to hook "${this.name}".`)
            return
        }

        // Remove existing one
        this.removeHandler(callback)

        // Add it
        this.handlers.push({ callback, priority })

        // Sort by priority
        this.handlers.sort((a, b) => a.priority - b.priority)

    }

    /** Remove a handler */
    removeHandler(callback) {
        this.handlers = this.handlers.filter(h => h.callback != callback)
    }

    /** 
     * Trigger this hook.
     * 
     * @param {object} data Any custom data to pass to the handlers.
     * @returns {any} Returns the first truthy response from hook handlers. Does not return promises.
     */
    trigger(data) {

        // Go through each handler
        for (let handler of this.handlers) {

            // Call it
            let result = handler.callback(data)
            if (result && !(result instanceof Promise))
                return result

        }

        // Not cancelled
        return false

    }

    /** 
     * Trigger this hook, but waits for any promises returned.
     * 
     * @param {object} data Any custom data to pass to the handlers.
     * @returns {Promise<boolean>} Truthy (the last handler's response) if the event was cancelled, false otherwise.
     */
    async triggerAsync(data) {

        // Go through each handler
        for (let handler of this.handlers) {

            // Call it
            let result = await handler.callback(data)
            if (result)
                return result

        }

        // Not cancelled
        return false

    }

    /** 
     * Trigger this hook and collect all truthy responses.
     * 
     * @param {object} data Any custom data to pass to the handlers.
     * @returns {any[]} All returned values.
     */
    triggerAll(data) {

        // Go through each handler
        let responses = []
        for (let handler of this.handlers) {

            // Call it and ignore errors
            try {
                let result = handler.callback(data)
                if (result)
                    responses.push(result)
            } catch (err) {
                console.warn(`[Hooks] Hook listener for '${this.name}' failed:`, err)
            }

        }

        // Done
        return responses

    }

    /** 
     * Trigger this hook and collect all truthy responses. Also waits for any asynchronous calls.
     * 
     * @param {object} data Any custom data to pass to the handlers.
     * @returns {Promise<any[]>} All returned values.
     */
    async triggerAllAsync(data) {

        // Go through each handler
        let responsePromises = []
        let responses = []
        for (let handler of this.handlers) {

            // Create promise
            responsePromises.push((async () => {

                // Call it and ignore errors
                try {
                    let result = await handler.callback(data)
                    if (result)
                        responses.push(result)
                } catch (err) {
                    console.warn(`[Hooks] Hook listener for '${this.name}' failed:`, err)
                }

            })())

        }

        // Wait for all promises to finish
        await Promise.all(responsePromises)

        // Done
        return responses

    }

}
